import type { WeekOpeningHours } from './opening-hours';
import { isIanaTimeZone } from './time-zone';

const MINUTES_PER_DAY = 24 * 60;
const MINUTE = 60_000;
const DAY = MINUTES_PER_DAY * MINUTE;
const DAYS: ReadonlyArray<{ key: keyof WeekOpeningHours; label: string }> = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

type Interval = { start: number; end: number };

const parseTime = (value: string): number | null => {
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)) return null;
  return Number(value.slice(0, 2)) * 60 + Number(value.slice(3));
};

const dayIndex = (localStamp: number): number => (new Date(localStamp).getUTCDay() + 6) % 7;

const formatTime = (localStamp: number): string => {
  const date = new Date(localStamp);
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  return `${hour % 12 || 12}${minute ? `:${String(minute).padStart(2, '0')}` : ''} ${hour >= 12 ? 'pm' : 'am'}`;
};

/**
 * Resolve regular hours in the business's named timezone. Repeated DST times use
 * the earliest opening and latest closing, so a session never closes at rollback.
 * Nonexistent times move forward by the DST gap; a collapsed session is skipped.
 */
export const resolveOpeningStatus = ({ hours, timeZone, now }: {
  hours?: WeekOpeningHours;
  timeZone?: string | null;
  now: Date;
}): { isOpen: boolean; text: string } | null => {
  if (!hours || !timeZone || !isIanaTimeZone(timeZone) || !Number.isFinite(now.getTime())) return null;

  const schedule = new Map<number, Interval>();
  for (const [index, day] of DAYS.entries()) {
    const opens = hours[day.key]?.opens?.trim() ?? '';
    const closes = hours[day.key]?.closes?.trim() ?? '';
    if (!opens && !closes) continue;
    const start = parseTime(opens);
    const end = parseTime(closes);
    // A partial or ambiguous schedule must never produce a confident live claim.
    if (start === null || end === null || start === end) return null;
    schedule.set(index, { start, end: end + (end < start ? MINUTES_PER_DAY : 0) });
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone, calendar: 'gregory', numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  // Represent civil fields as a UTC-shaped timestamp solely for date arithmetic.
  const localStamp = (instant: number): number => {
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value }) => [type, value]));
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second));
  };
  const current = now.getTime();
  const currentLocal = localStamp(current);
  const today = Math.floor(currentLocal / DAY) * DAY;
  // Bound work to nearby dates, sampling offsets once rather than scanning minutes.
  const offsets = new Set<number>();
  for (let day = -2; day <= 9; day += 1) {
    const instant = today + day * DAY;
    offsets.add(localStamp(instant) - instant);
  }
  const resolveBoundary = (wallTime: number, opening: boolean): number => {
    const candidates = [...offsets].map((offset) => {
      const instant = wallTime - offset;
      return { instant, local: localStamp(instant) };
    });
    const matches = candidates.filter((candidate) => candidate.local === wallTime).map((candidate) => candidate.instant);
    if (matches.length) return opening ? Math.min(...matches) : Math.max(...matches);
    // In a gap, an offset from before the change maps to the first shifted time.
    return Math.min(...candidates.filter((candidate) => candidate.local > wallTime).map((candidate) => candidate.instant));
  };

  const intervals: Interval[] = [];
  // Include yesterday's overnight session and a complete future weekly cycle.
  for (let day = -1; day <= 7; day += 1) {
    const date = today + day * DAY;
    const times = schedule.get(dayIndex(date));
    if (!times) continue;
    const start = resolveBoundary(date + times.start * MINUTE, true);
    const end = resolveBoundary(date + times.end * MINUTE, false);
    if (end > start) intervals.push({ start, end });
  }
  const merged: Interval[] = [];
  for (const interval of intervals.sort((a, b) => a.start - b.start)) {
    const previous = merged.at(-1);
    if (previous && interval.start <= previous.end) previous.end = Math.max(previous.end, interval.end);
    else merged.push({ ...interval });
  }

  const open = merged.find((interval) => interval.start <= current && current < interval.end);
  if (open) {
    const closing = localStamp(open.end);
    const closingDay = Math.floor(closing / DAY) - Math.floor(today / DAY);
    const label = closingDay === 1 ? 'tomorrow' : DAYS[dayIndex(closing)].label;
    const time = closing - currentLocal >= DAY ? `${label} at ${formatTime(closing)}` : formatTime(closing);
    return { isOpen: true, text: `Open now · until ${time}` };
  }

  const next = merged.find((interval) => interval.start > current);
  if (!next) return null;
  const opening = localStamp(next.start);
  const daysAway = Math.floor(opening / DAY) - Math.floor(today / DAY);
  const label = daysAway === 0 ? 'today' : daysAway === 1 ? 'tomorrow' : DAYS[dayIndex(opening)].label;
  return { isOpen: false, text: `Closed · opens ${label} at ${formatTime(opening)}` };
};
